import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { useShellSolutionId } from "src/components/AppShell/shellContext";
import { ROUTES } from "src/routes/paths";
import { RequirementsDialog } from "src/workspace/RequirementsDialog";
import { useWorkspace } from "src/workspace/useWorkspace";

const SEGMENT_SX = {
	px: 2,
	minHeight: 36,
	borderRadius: "999px",
	fontSize: 14,
	fontWeight: 500,
	color: "text.primary",
	textDecoration: "none",
	display: "inline-flex",
	alignItems: "center",
	whiteSpace: "nowrap",
	"&.active": { bgcolor: "background.paper", color: "primary.main", fontWeight: 600, boxShadow: 1 }
} as const;

/**
 * Figma D05/D14 top-bar pill: the solution and its counts (opening the requirements context) plus the
 * Map / Solution overview switch. Shown while a page declares its solution with `useSolutionContext`.
 */
export const SolutionPill: FC = () => {
	const { t } = useTranslation();
	const solutionId = useShellSolutionId();
	const workspace = useWorkspace(solutionId);
	const [requirementsOpen, setRequirementsOpen] = useState(false);
	const openRequirements = useCallback(() => setRequirementsOpen(true), []);
	const closeRequirements = useCallback(() => setRequirementsOpen(false), []);

	if (!solutionId || !workspace.data) return null;
	const { solution, counts } = workspace.data;
	const countText = [
		t("workspace.count.offices", { count: counts.offices }),
		t("workspace.count.metros", { count: counts.metros }),
		t("workspace.count.clouds", { count: counts.clouds }),
		t("workspace.count.apps", { count: counts.apps })
	].join("  |  ");

	return (
		<>
			<Box
				sx={{
					display: { xs: "none", md: "flex" },
					alignItems: "center",
					gap: 0.5,
					p: 0.5,
					pl: 2,
					minWidth: 0,
					borderRadius: "999px",
					bgcolor: "background.default",
					border: 1,
					borderColor: "divider"
				}}>
				<ButtonBase
					onClick={openRequirements}
					aria-label={t("workspace.requirements.open")}
					sx={{ gap: 1.25, minWidth: 0, mr: 1, borderRadius: "999px", justifyContent: "flex-start" }}>
					<Typography noWrap sx={{ fontSize: 14, fontWeight: 600, maxWidth: 220 }}>
						{solution.name}
					</Typography>
					<Typography
						noWrap
						color="text.secondary"
						sx={{ fontSize: 13, whiteSpace: "pre", display: { md: "none", lg: "block" } }}>
						{countText}
					</Typography>
				</ButtonBase>
				<Box component={NavLink} to={ROUTES.solutionDesign(solution.id)} sx={SEGMENT_SX}>
					{t("workspace.map")}
				</Box>
				<Box component={NavLink} to={ROUTES.solutionOverview(solution.id)} sx={SEGMENT_SX}>
					{t("workspace.overview")}
				</Box>
			</Box>
			<RequirementsDialog workspace={workspace.data} open={requirementsOpen} onClose={closeRequirements} />
		</>
	);
};
